import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import AppContext from 'context/Context';
import { createPortal } from 'react-dom';

const VIEWPORT_PADDING = 8;

const getDropClassName = drop => {
  if (drop === 'start') {
    return 'dropstart';
  }

  if (drop === 'end') {
    return 'dropend';
  }

  if (drop === 'up') {
    return 'dropup';
  }

  return 'dropdown';
};

const getPlacement = (drop, align) => {
  if (drop === 'start') {
    return 'left-start';
  }

  if (drop === 'end') {
    return 'right-start';
  }

  if (drop === 'up') {
    return align === 'end' ? 'top-end' : 'top-start';
  }

  return align === 'end' ? 'bottom-end' : 'bottom-start';
};

const clamp = (value, min, max) => {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
};

const CardDropdown = ({
  align,
  btnRevealClass,
  drop,
  children,
  icon = 'ellipsis-h',
  iconClassName = 'fs--2',
  menuClassName,
  menuStyle
}) => {
  const appContext = useContext(AppContext);
  const wrapperRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [show, setShow] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const isRTL = appContext?.config?.isRTL || appContext?.isRTL || false;
  const isSideDrop = drop === 'start' || drop === 'end';
  const defaultAlign = isSideDrop ? 'start' : isRTL ? 'start' : 'end';
  const dropdownAlign = align || defaultAlign;

  const updateMenuPosition = useCallback(() => {
    if (!toggleRef.current || !menuRef.current) {
      return;
    }

    const rect = toggleRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current.offsetWidth || 160;
    const menuHeight = menuRef.current.offsetHeight || 40;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    let top = rect.bottom;
    let left = dropdownAlign === 'end' ? rect.right - menuWidth : rect.left;

    if (drop === 'start') {
      top = rect.top;
      left = rect.left - menuWidth;
    } else if (drop === 'end') {
      top = rect.top;
      left = rect.right;
    } else if (drop === 'up') {
      top = rect.top - menuHeight;
    }

    setMenuPosition({
      top: clamp(
        top,
        VIEWPORT_PADDING,
        viewportHeight - menuHeight - VIEWPORT_PADDING
      ),
      left: clamp(
        left,
        VIEWPORT_PADDING,
        viewportWidth - menuWidth - VIEWPORT_PADDING
      )
    });
  }, [drop, dropdownAlign]);

  useLayoutEffect(() => {
    if (show) {
      updateMenuPosition();
    } else {
      setMenuPosition(null);
    }
  }, [show, updateMenuPosition]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const closeOnOutsidePointer = event => {
      if (
        wrapperRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      setShow(false);
    };

    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setShow(false);
      }
    };

    const reposition = () => {
      updateMenuPosition();
    };

    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [show, updateMenuPosition]);

  const closeOnMenuItemClick = event => {
    if (event.target.closest('.dropdown-item')) {
      setShow(false);
    }
  };

  const menu =
    show &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        ref={menuRef}
        className={classNames('dropdown-menu show border py-0', menuClassName, {
          'dropdown-menu-end': dropdownAlign === 'end' && !isSideDrop
        })}
        x-placement={getPlacement(drop, dropdownAlign)}
        data-popper-placement={getPlacement(drop, dropdownAlign)}
        onClick={closeOnMenuItemClick}
        style={{
          position: 'fixed',
          top: menuPosition ? `${menuPosition.top}px` : 0,
          left: menuPosition ? `${menuPosition.left}px` : 0,
          zIndex: 1080,
          visibility: menuPosition ? 'visible' : 'hidden',
          ...menuStyle
        }}
      >
        {children}
        {!children && (
          <div className="py-2">
            <Dropdown.Item>View</Dropdown.Item>
            <Dropdown.Item>Export</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="text-danger">Remove</Dropdown.Item>
          </div>
        )}
      </div>,
      document.body
    );

  return (
    <div
      ref={wrapperRef}
      className={classNames(
        'font-sans-serif btn-reveal-trigger',
        getDropClassName(drop)
      )}
    >
      <button
        ref={toggleRef}
        type="button"
        aria-expanded={show}
        aria-haspopup="true"
        className={classNames('btn btn-link btn-sm text-600', {
          [btnRevealClass]: btnRevealClass,
          'btn-reveal': !btnRevealClass
        })}
        onClick={() => setShow(!show)}
      >
        <FontAwesomeIcon icon={icon} className={iconClassName} />
      </button>
      {menu}
    </div>
  );
};

CardDropdown.propTypes = {
  align: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  btnRevealClass: PropTypes.string,
  drop: PropTypes.string,
  children: PropTypes.node,
  flip: PropTypes.bool,
  icon: PropTypes.string,
  iconClassName: PropTypes.string,
  menuClassName: PropTypes.string,
  menuStyle: PropTypes.object,
  popperConfig: PropTypes.object
};

export default CardDropdown;
